import React from "react";
import { Plus, Trash2 } from "lucide-react";
import { RevenueMixChart } from "./charts";
import * as m from "../lib/model";

const PRESETS = ["Commercial contracts", "Residential", "Municipal / Government", "New installation", "Service & repair",
  "Parts & accessories", "Subscription / recurring", "One-off projects"];

export const RevenueMixEditor = ({ mix, revenue, onChange }) => {
  const rows = mix || [];
  const total = rows.reduce((s, r) => s + (Number(r.value) || 0), 0);
  const off = rows.length > 0 && Math.abs(total - 100) > 0.5;

  const set = (i, key, val) => onChange(rows.map((r, j) => (j === i ? { ...r, [key]: val } : r)));
  const add = () => onChange([...rows, { label: PRESETS[rows.length % PRESETS.length], value: Math.max(0, Math.round((100 - total) * 10) / 10) }]);
  const del = (i) => onChange(rows.filter((_, j) => j !== i));
  const normalise = () => (total > 0 ? onChange(rows.map((r) => ({ ...r, value: Math.round(((Number(r.value) || 0) / total) * 1000) / 10 }))) : null);

  return (
    <div className="grid g2" data-testid="revenue-mix-editor">
      <div>
        <div className="section-label">Customer / revenue type</div>
        <table className="tbl rm-tbl">
          <thead><tr><th>Type</th><th className="num">% of revenue</th><th className="num">Revenue</th><th /></tr></thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} data-testid={`rm-row-${i}`}>
                <td><input className="inp" value={r.label || ""} placeholder="e.g. Commercial contracts"
                  onChange={(e) => set(i, "label", e.target.value)} data-testid={`rm-label-${i}`} /></td>
                <td className="num"><input className="inp num rm-pct" type="number" step="0.5" min="0" max="100" value={r.value ?? ""}
                  onChange={(e) => set(i, "value", e.target.value === "" ? "" : Number(e.target.value))} data-testid={`rm-value-${i}`} /></td>
                <td className="num mono">{m.fmt$(((Number(r.value) || 0) / 100) * (revenue || 0))}</td>
                <td className="num"><button className="icon-btn" onClick={() => del(i)} title="Remove" data-testid={`rm-delete-${i}`}><Trash2 size={13} /></button></td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={4} style={{ color: "var(--text3)" }}>No revenue mix recorded — add the customer types that make up revenue.</td></tr>}
            <tr className="total" data-testid="rm-total">
              <td>Total</td>
              <td className="num" style={{ color: off ? "var(--red)" : undefined }}>{total.toFixed(1)}%</td>
              <td className="num mono">{m.fmt$((total / 100) * (revenue || 0))}</td><td />
            </tr>
          </tbody>
        </table>
        <div className="tr-actions" style={{ marginTop: 10 }}>
          <button className="btn btn-primary btn-sm" onClick={add} data-testid="rm-add"><Plus size={12} style={{ marginRight: 5 }} />Add type</button>
          {off && <button className="btn btn-sm" onClick={normalise} data-testid="rm-normalise">Scale to 100%</button>}
        </div>
        <div className="ra-sub" style={{ marginTop: 8 }}>
          {off ? `Shares add to ${total.toFixed(1)}% — scale them to 100% so the dollar split is right.`
            : "Shares of TTM revenue. This mix shows on the Dashboard and in the investor deal room."}
        </div>
      </div>
      <div>
        <div className="section-label">Mix</div>
        {rows.length ? <RevenueMixChart mix={rows.filter((r) => Number(r.value) > 0)} /> : <div className="bullet">Add a type to see the split.</div>}
      </div>
    </div>
  );
};

/** Read-only bars — used on the Dashboard fallback and in the investor room. */
export const RevenueMixBars = ({ mix, revenue, testid = "revenue-mix-bars" }) => {
  const rows = (mix || []).filter((r) => Number(r.value) > 0);
  if (!rows.length) return null;
  const total = rows.reduce((s, r) => s + Number(r.value), 0) || 100;
  return (
    <div className="rm-bars" data-testid={testid}>
      {rows.map((r, i) => (
        <div className="rm-bar-row" key={i} data-testid={`rm-bar-${i}`}>
          <span className="rm-bar-l">{r.label}</span>
          <span className="rm-bar-track"><i style={{ width: `${(Number(r.value) / total) * 100}%` }} /></span>
          <span className="rm-bar-v mono">{Number(r.value).toFixed(1)}%</span>
          {revenue > 0 && <span className="rm-bar-d mono">{m.fmt$((Number(r.value) / 100) * revenue)}</span>}
        </div>
      ))}
    </div>
  );
};
